"use client";

import * as React from "react";
import { ThemeToggle } from "../theme/index.ts";
import { Card } from "../components/ui/card.tsx";
import { cn } from "../lib/cn.ts";

export interface AuthShellProps {
  brand?: {
    logo?: React.ReactNode;
    name?: string;
  };
  title: string;
  description?: string;
  subtitle?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  variant?: "platform" | "store" | "centered";
  aside?: React.ReactNode;
  className?: string;
}

export function AuthShell({
  brand,
  title,
  description,
  subtitle,
  children,
  footer,
  variant = "platform",
  aside,
  className,
}: AuthShellProps) {
  const desc = description ?? subtitle;
  const brandName = brand?.name;
  const brandLogo = brand?.logo;

  return (
    <div
      className={cn(
        "w-full flex bg-[var(--background)] text-[var(--foreground)] relative",
        // A store's sign-in sits inside the store's header and bottom bar: it fills what is left, centred, and does not scroll.
        variant === "store" ? "min-h-[calc(100dvh-10.5rem)] md:min-h-[calc(100dvh-8rem)]" : "min-h-screen",
        className,
      )}
    >
      {/* Staff screens can switch theme; a store's customer sign-in is always light (no dark store theme yet). */}
      {variant === "store" ? null : (
        <div className="absolute top-4 right-4 z-50">
          <ThemeToggle />
        </div>
      )}

      <div className={cn("flex-1 flex flex-col justify-center items-center px-4 sm:px-6 lg:px-8", variant === "store" ? "py-4" : "py-12")}>
        <div className="w-full max-w-[400px] space-y-6">
          {/* Brand header */}
          <div className="flex flex-col items-center text-center space-y-2">
            {brandLogo ? (
              <div className="h-10 w-10 flex items-center justify-center">
                {brandLogo}
              </div>
            ) : null}
            {brandName && variant !== "store" ? (
              <div className="text-xs font-semibold tracking-wider uppercase text-[var(--muted-foreground)]">
                {brandName}
              </div>
            ) : null}
            <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]">
              {title}
            </h1>
            {desc && variant !== "store" && (
              <p className="text-xs text-[var(--muted-foreground)] text-balance">
                {desc}
              </p>
            )}
          </div>

          {/* Form container */}
          <Card className="p-6 border border-[var(--border)] shadow-xs rounded-[var(--radius)] bg-[var(--card)]">
            {children}
          </Card>

          {/* A store's customer pages keep the card first; the explanation sits under it. */}
          {desc && variant === "store" && (
            <p className="text-center text-xs text-[var(--muted-foreground)] text-balance">{desc}</p>
          )}

          {/* Optional Footer */}
          {footer && (
            <div className="text-center text-xs text-[var(--muted-foreground)]">
              {footer}
            </div>
          )}
        </div>
      </div>

      {/* Optional Aside Desktop split panel */}
      {aside && (
        <div className="hidden lg:flex flex-1 relative bg-[var(--muted)] border-l border-[var(--border)] items-center justify-center p-12 overflow-hidden">
          {aside}
        </div>
      )}
    </div>
  );
}
